import { useState, useRef, useCallback, useEffect } from 'react';
import { useNavigate, useBlocker } from 'react-router-dom';

interface UseNavigationGuardOptions {
  isDirty: boolean;
  fallbackPath?: string;
  /**
   * Set this when `fallbackPath` is exactly the page this form was pushed
   * from (e.g. a List/Detail page that navigated here via `navigate(path)`).
   * In that case, replacing the current entry with `fallbackPath` would create
   * two consecutive history entries with the same URL — the first browser/app
   * back press then appears to do nothing (it only moves the history index,
   * URL doesn't change) and a second press is needed to actually go anywhere.
   * When true, Cancel/Discard instead does `navigate(-1)`, which correctly
   * pops this page off the stack without creating a duplicate entry.
   */
  backOnCancel?: boolean;
}

/**
 * Blocks in-app navigation (browser back, sidebar links, programmatic navigate)
 * when a form has unsaved changes, and shows a discard-confirmation modal.
 *
 * Returns everything the caller needs to wire up the modal and a Cancel button.
 */
export function useNavigationGuard({ isDirty, fallbackPath = '/', backOnCancel = false }: UseNavigationGuardOptions) {
  const navigate = useNavigate();
  const [isDiscardModalOpen, setIsDiscardModalOpen] = useState(false);
  const [pendingNavigationPath, setPendingNavigationPath] = useState<string | null>(null);
  const isAllowedRef = useRef(false);
  // Tracks whether the pending discard originated from the Cancel button
  // (safe to use navigate(-1)) vs. an intercepted browser/router navigation
  // (must honor the exact destination the user/browser was navigating to).
  const isManualCancelRef = useRef(false);

  const blocker = useBlocker(
    useCallback(
      ({ currentLocation, nextLocation }: { currentLocation: any; nextLocation: any }) => {
        if (isAllowedRef.current) return false;
        return isDirty && currentLocation.pathname !== nextLocation.pathname;
      },
      [isDirty]
    )
  );

  useEffect(() => {
    if (blocker.state === 'blocked') {
      isManualCancelRef.current = false;
      setPendingNavigationPath(blocker.location.pathname);
      setIsDiscardModalOpen(true);
    }
  }, [blocker.state]);

  const handleCancel = useCallback(() => {
    if (isDirty) {
      isManualCancelRef.current = true;
      setPendingNavigationPath(fallbackPath);
      setIsDiscardModalOpen(true);
      return;
    }
    if (backOnCancel) {
      // Pop this page off the stack instead of replacing — see backOnCancel doc.
      navigate(-1);
      return;
    }
    // Replace instead of push so this add/edit page doesn't linger in the
    // history stack — otherwise the next back press (mobile app bar or
    // browser back) would land back on this page instead of its parent.
    navigate(fallbackPath, { replace: true });
  }, [isDirty, fallbackPath, backOnCancel, navigate]);

  const handleDiscardChanges = useCallback(() => {
    setIsDiscardModalOpen(false);
    const targetPath = pendingNavigationPath;
    const wasManualCancel = isManualCancelRef.current;
    setPendingNavigationPath(null);
    isManualCancelRef.current = false;

    isAllowedRef.current = true;
    if (blocker.state === 'blocked') {
      blocker.reset();
    }

    if (backOnCancel && wasManualCancel) {
      // Pop this page off the stack instead of replacing — see backOnCancel doc.
      navigate(-1);
    } else {
      // See note in handleCancel — replace so this page is removed from history.
      navigate(targetPath ?? fallbackPath, { replace: true });
    }
    setTimeout(() => {
      isAllowedRef.current = false;
    }, 100);
  }, [pendingNavigationPath, blocker, navigate, fallbackPath, backOnCancel]);

  const handleDiscardModalClose = useCallback(() => {
    setIsDiscardModalOpen(false);
    setPendingNavigationPath(null);
    if (blocker.state === 'blocked') {
      blocker.reset();
    }
  }, [blocker]);

  /** Call before a successful-submit navigation so the blocker won't interfere. */
  const allowNavigation = useCallback(() => {
    isAllowedRef.current = true;
    setTimeout(() => {
      isAllowedRef.current = false;
    }, 100);
  }, []);

  return {
    isDiscardModalOpen,
    handleCancel,
    handleDiscardChanges,
    handleDiscardModalClose,
    allowNavigation
  };
}
